// Sprint ONBOARD ONB.C3 (A.5, #831) — Settings → Google Drive surface.
//
// Mounted at /settings/integrations/gdrive. Reuses AuthorizeGDrivePage in
// 'settings' mode, which opens on the DRV-5a connection panel (status,
// Reconnect, Disconnect) and returns to that panel after a connect. A connect
// started here DOES complete the wizard's authorize_gdrive task when the user
// has an active setup project in this tenant: the callback resolves it
// server-side (driveOauth.ts, getActiveForUser → completeTaskByType), whichever
// page started the flow (#3038). Wrapped in the standard Settings sidebar chrome.
//
// ADMIN-ONLY per the #1595 hybrid Member-Settings-Visibility decision + the
// #1594 fix: this route is reachable directly by URL even with the Storage
// card/page hidden from members, so it needs its own fail-closed guard rather
// than relying solely on Storage's. The role check does NOT live inside
// AuthorizeGDrivePage itself — that component is shared with the onboarding
// wizard's /setup/authorize-gdrive (mode='setup'), which must stay
// unaffected; gating this Settings-only wrapper instead keeps the change
// scoped to the surface that was actually the gap. The load-bearing gate is
// still the backend (driveOauth.ts's TENANT_ADMIN-gated /authorize + the
// fresh role re-verification at /callback) — this is defense-in-depth so a
// member never even sees the flow start.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import AuthorizeGDrivePage from './AuthorizeGDrivePage';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

export default function SettingsDriveAuthPage() {
  const navigate = useNavigate();
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const isAdmin = isAdminRole(myTenant?.role);

  // Fail CLOSED — a non-admin OR a null/failed membership redirects to
  // /settings rather than rendering the Drive-authorization surface.
  useEffect(() => {
    if (!tenantLoading && !isAdmin) navigate('/settings');
  }, [tenantLoading, isAdmin, navigate]);

  // Fail-closed placeholder: non-admin / null membership is being redirected.
  if (!isAdmin) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
        <Sidebar />
        <main style={{ flex: 1, padding: 32 }}>
          <p data-testid="drive-auth-redirecting" style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>
            Redirecting…
          </p>
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar />
      <main style={{ flex: 1, overflow: 'auto' }}>
        <AuthorizeGDrivePage mode="settings" />
      </main>
    </div>
  );
}
